// The style switcher: a square-cornered segmented switch with "Bold", "Clinical", "Playful" and "Theatre". It sets this device's preview of a look, never the battle's
// own look (that is chosen, and can be frozen, in the Battle builder).
//   variant "staff"    always shown: the Battle builder, host and ops pages place it themselves.
//   variant "preview"  the floating switch LookLayer shows on phones and the stage, only while a preview is on.
// When the battle's look is locked the segments are disabled and say so; ordinary players never see the switch.
import { Lock, X } from 'lucide-react';
import { LOOKS, LOOK_LABEL, type Look } from './look';
import { useLook, useSettingsLookFromKey, useStaffSwitch } from './store';

export interface LookSwitchProps {
  variant?: 'staff' | 'preview';
  /** Staff pages without a battle: pass the ops key and the switch reads the global look setting itself. */
  opsKey?: string;
  className?: string;
}

export function LookSwitch({ variant = 'staff', opsKey, className = '' }: LookSwitchProps) {
  const { look, locked, previewing, setPreview } = useLook();
  const staff = variant === 'staff';
  useStaffSwitch(staff);
  useSettingsLookFromKey(staff ? opsKey : undefined);
  const choose = (l: Look) => {
    if (l === look && !previewing) return; // already showing it: nothing to preview
    setPreview(l);
  };
  return (
    <div className={`rd-look-switch inline-flex flex-wrap items-center gap-x-2 gap-y-1 ${className}`} data-variant={variant} data-locked={locked || undefined}>
      {!staff && <span className="pl-2 text-xs font-bold text-ink-2">Preview</span>}
      <div role="group" aria-label="Look" className={`rd-look-seg inline-flex items-center gap-1 rounded-badge bg-surface-2 p-1 ${staff ? 'border border-line' : ''}`}>
        {LOOKS.map((l) => {
          const on = look === l;
          return (
            <button key={l} type="button" aria-pressed={on} disabled={locked} data-look-option={l} onClick={() => choose(l)}
              className={`inline-flex min-h-11 items-center justify-center rounded-[8px] font-semibold transition-colors duration-150 disabled:cursor-not-allowed ${staff ? 'px-4 text-sm' : 'px-3 text-[13px]'} ${on ? 'bg-surface text-ink shadow-lift' : 'text-ink-2 hover:text-ink disabled:hover:text-ink-2'}`}>
              {LOOK_LABEL[l]}
            </button>
          );
        })}
      </div>
      {locked && (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-ink-2">
          <Lock aria-hidden="true" className="size-3.5" strokeWidth={2.4} />Fixed for this battle
        </span>
      )}
      {staff && previewing && (
        <button type="button" onClick={() => setPreview(undefined)}
          className="inline-flex min-h-11 items-center px-2 text-sm font-semibold text-iv-strong underline-offset-4 hover:underline">End preview</button>
      )}
      {!staff && (
        <button type="button" aria-label="End preview" onClick={() => setPreview(undefined)}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink">
          <X aria-hidden="true" className="size-5" strokeWidth={2.4} />
        </button>
      )}
    </div>
  );
}
